<template>
  <GloboGraphicsModulo
    icon="mdi-eye-outline"
    title="Preview"
    action-icon="mdi-open-in-new"
    action-label="Abrir cena em nova aba"
    :offline="offline"
    @action="openCena"
  >
    <!-- Idle: nothing selected and nothing on air. -->
    <div v-if="!showFrame" class="gg-empty-box">
      <span class="gg-empty-box__text">Selecione uma playlist, template ou cena para visualizar</span>
    </div>

    <div v-else class="gg-preview">
      <div class="gg-preview__frame">
        <!-- Native engine render over WebRTC: program mirror when on air, or the
             cued preview engine before playout. -->
        <WebRtcPlayer
          v-if="livePreviewUrl"
          :key="livePreviewUrl"
          :signaling-url="livePreviewUrl"
          :controls="false"
        />
        <!-- Cue in flight -->
        <div v-else-if="cueLoading" class="gg-preview__frame-empty">
          <v-progress-circular indeterminate size="28" width="3" color="primary" />
          <span class="gg-preview__hint">Preparando pré-visualização ao vivo…</span>
        </div>
        <!-- Static still fallback -->
        <v-img
          v-else-if="previewSrc"
          :key="previewSrc"
          :src="previewSrc"
          contain
          @error="onPreviewError"
        >
          <template #placeholder>
            <div class="d-flex align-center justify-center fill-height">
              <v-progress-circular indeterminate size="28" width="3" color="primary" />
            </div>
          </template>
        </v-img>
        <div v-else class="gg-preview__frame-empty">
          <v-icon large color="text_secondary">mdi-image-off-outline</v-icon>
          <span class="gg-preview__hint">Sem pré-visualização disponível</span>
        </div>
      </div>

      <!-- What the frame is showing, and the way out of it. On air the name is the
           program's on-air graphic; otherwise it's the selected item. Clearing is
           only offered for a SELECTION — the on-air graphic is dropped with Takeout,
           not by tidying the preview, and offering it here would imply otherwise. -->
      <div v-if="displayName" class="gg-preview__footer">
        <div class="gg-preview__name">{{ displayName }}</div>
        <button
          v-if="previewItem"
          type="button"
          class="gg-preview__clear"
          @click="clearPreviewSelection"
        >
          <v-icon size="20">mdi-monitor-off</v-icon>
          <span>Limpar preview</span>
        </button>
      </div>
    </div>
  </GloboGraphicsModulo>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  playoutItemPreviewUrl,
  playoutItemMosThumbnailUrl,
} from "@/utils/sceneUtils";
import { getVizrtThumbnailUrl } from "@/store/modules/globographics";
import WebRtcPlayer from "@/components/WebRtcPlayer.vue";
import GloboGraphicsModulo from "./GloboGraphicsModulo.vue";

// Width requested for the MSE media:thumbnail so the static fallback isn't a soft
// upscale of the list thumbnail's ~256px default. Frame is 640 CSS px → ~1280
// physical on a HiDPI screen, so render at 1280.
const BIG_PREVIEW_WIDTH = 1280;

export default {
  name: "GloboGraphicsCenaEmExibicao",
  components: { WebRtcPlayer, GloboGraphicsModulo },
  props: {
    cod_producao: { type: Number, required: true },
    offline: { type: Boolean, default: false },
  },
  data() {
    return {
      // Cache-buster bumped only when the selected item's content actually changes,
      // so the static fallback reloads without re-fetching on every poll tick.
      previewBust: 0,
      previewError: false,
      heartbeatTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      previewItem: "playout/getterPreviewItem",
      playoutState: "playout/getterPlayoutState",
      previewCue: "playout/getterPreviewCue",
      cueLoading: "playout/getterPreviewCueLoading",
    }),
    // Render the frame when there's a selection OR a program feed to mirror.
    showFrame() {
      return !!this.previewItem || this.engineAllocated;
    },
    programSignalingUrl() {
      return this.playoutState?.playout?.signalingUrl || null;
    },
    // An engine is allocated to this production — i.e. the playlist is ACTIVE.
    engineAllocated() {
      return !!this.programSignalingUrl;
    },
    // A graphic is genuinely ON AIR (an item taken), distinct from merely active.
    isOnAir() {
      return !!this.playoutState?.onAir;
    },
    // The native cue result, only if it's for the item currently selected.
    previewCueUrl() {
      const c = this.previewCue;
      const it = this.previewItem;
      return c && it && c.idItem === it.id_playlist_item ? c.signalingUrl : null;
    },
    // A cued engine preview is live → the engine lease must be heartbeated to hold it.
    hasActiveCue() {
      return !this.isOnAir && !!this.previewCueUrl;
    },
    // The live WebRTC surface: ON AIR → the program engine (the on-air graphic + live
    // edits); otherwise → the cued preview for the SELECTED item (the same engine when
    // active, a free engine when not), and null when nothing is selected. Keying the
    // idle case on the selection — not merely on the playlist being active — means
    // deselecting instantly drops the video (the monitor stops watching the engine),
    // so the clear feels immediate rather than making the operator watch the engine's
    // out animation play. The engine still outs in the background, unseen.
    livePreviewUrl() {
      if (this.isOnAir) return this.programSignalingUrl;
      return this.previewItem ? this.previewCueUrl : null;
    },
    isMosPreview() {
      return this.previewItem && this.previewItem.id_gh_cena == null;
    },
    previewTemplateId() {
      const it = this.previewItem;
      if (!it) return null;
      return it.dsc_vizrt_template_id || (it.cena && it.cena.dsc_vizrt_template_id) || null;
    },
    // The name of whatever the frame is showing: the on-air graphic when something is
    // taken, otherwise the selected (previewing) item. Blank when the engine monitor
    // is dark (active, nothing taken, nothing selected) — nothing to name.
    displayName() {
      if (this.isOnAir) return this.playoutState?.onAir?.nome || "";
      const it = this.previewItem;
      if (!it) return "";
      return (it.cena && it.cena.dsc_nome_cena) || it.dsc_titulo || "Cena sem nome";
    },
    // A change signal for the selected item: whenever its render inputs change, so
    // does this string → the watcher reloads the still and re-cues the live preview.
    // Includes the native cena's values so an inline native edit re-cues too.
    previewSignal() {
      const it = this.previewItem;
      if (!it) return "";
      return [
        it.id_playlist_item,
        it.dsc_preview_hash || "",
        it.dsc_mos_thumbnail_url || "",
        it.dsc_valores_campos ? JSON.stringify(it.dsc_valores_campos) : "",
        it.cena && it.cena.dsc_valores_campos ? JSON.stringify(it.cena.dsc_valores_campos) : "",
      ].join("|");
    },
    // Cue a real PLAYLIST item (has id_playlist_item) whenever nothing is on air —
    // both before activation (free engine) and in the active-but-nothing-taken window
    // (the idle program engine). Never while on air: there the engine output IS the
    // program, so the block mirrors it instead. A selected saved cena (Templates e
    // cenas block) has no id_playlist_item → no cue, just its static render. Keyed on
    // previewSignal so an edit re-cues.
    cueTrigger() {
      return this.previewItem && this.previewItem.id_playlist_item && !this.isOnAir
        ? this.previewSignal
        : "";
    },
    // Static still fallback, mirroring PlaylistItemList.itemThumb at full size.
    previewSrc() {
      const it = this.previewItem;
      if (!it || this.previewError) return null;
      const bust = this.previewBust || undefined;
      if (this.isMosPreview) {
        if (it.dsc_mos_thumbnail_url) return playoutItemMosThumbnailUrl(it, bust, BIG_PREVIEW_WIDTH);
        if (it.dsc_preview_gcs_path) return playoutItemPreviewUrl(it, bust);
      }
      return getVizrtThumbnailUrl(this.previewTemplateId);
    },
  },
  watch: {
    previewSignal(next, prev) {
      if (next !== prev) {
        this.previewError = false;
        this.previewBust = Date.now();
      }
    },
    cueTrigger(next) {
      if (next) {
        this.cuePreviewItem({ idItem: this.previewItem.id_playlist_item });
      } else {
        this.stopPreviewCue();
      }
    },
    // Run the lease heartbeat only while an engine preview is actually live (a cued
    // item, not the program mirror). Start/stop the timer as that state flips.
    hasActiveCue(next) {
      if (next) this.startHeartbeat();
      else this.stopHeartbeat();
    },
  },
  beforeDestroy() {
    this.stopHeartbeat();
    this.stopPreviewCue();
  },
  methods: {
    ...mapActions({
      cuePreviewItem: "playout/cuePreviewItem",
      stopPreviewCue: "playout/stopPreviewCue",
      heartbeatPreviewCue: "playout/heartbeatPreviewCue",
      clearPreviewSelection: "playout/clearPreviewSelection",
    }),
    // Header action — pop the playout display open in a new tab (mirrors the
    // toolbar's "Cena" link).
    openCena() {
      const route = this.$router.resolve({ name: "cena" });
      window.open(route.href, "_blank", "noopener");
    },
    onPreviewError() {
      this.previewError = true;
    },
    // Keep the shared engine reserved while previewing. Paused when the tab is hidden
    // (not actively previewing → let the lease expire so another production can take
    // the engine). Cadence is well under the backend lease TTL (~20s).
    startHeartbeat() {
      this.stopHeartbeat();
      this.heartbeatTimer = setInterval(() => {
        if (document.hidden) return;
        this.heartbeatPreviewCue();
      }, 8000);
    },
    stopHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-preview {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-3);
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
}

// The frame takes whatever height the module has left rather than deriving it from
// its own width — a 16:9 box sized off the (wide) right column comes out taller than
// the block, which forced the operator to drag the module open just to see the whole
// graphic. The render is contained inside instead, so it is always fully visible and
// simply gets bigger as the block grows.
.gg-preview__frame {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: 120px;
  border-radius: var(--sc-radius-sm);
  overflow: hidden;
  // Dark backing so a lower-third's genuine transparency reads correctly.
  background: var(--sc-gg-overlay-image);

  // Any surface (WebRTC player, still image, placeholder) fills the frame.
  > * {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  // Letterbox rather than stretch: the frame is no longer guaranteed to be 16:9,
  // and a distorted graphic is worse than dark bars.
  ::v-deep video,
  ::v-deep img {
    object-fit: contain;
  }
}

.gg-preview__frame-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--sc-gg-text-tertiary);

  .v-icon {
    color: var(--sc-gg-text-tertiary) !important;
  }
}

.gg-preview__hint {
  margin-top: var(--sc-space-2);
  font-size: 12px;
  color: var(--sc-gg-text-tertiary);
}

.gg-preview__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
}

.gg-preview__name {
  font-size: 18px;
  font-weight: var(--sc-weight-medium);
  line-height: 1.3;
  letter-spacing: -1.5px;
  color: var(--sc-gg-text-title);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gg-preview__clear {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-1);
  flex-shrink: 0;
  font-size: 16px;
  font-weight: var(--sc-weight-medium);
  line-height: 20px;
  letter-spacing: 0.16px;
  color: var(--sc-gg-accent);
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  // The global `p, span, label` rule paints labels with the theme's secondary text
  // colour and outranks inheritance, so without this the icon is accent and the word
  // beside it is grey.
  span {
    color: inherit;
  }

  &:hover {
    opacity: 0.88;
  }
}
</style>
